/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';

export interface PasswordDialogData {
  displayName: string;
}

/**
 * Sets a new password for someone. It replaces a `window.prompt`, which showed
 * the password in clear text, asked for it once and accepted anything: here it
 * is masked, typed twice, and held to a minimum length before it is sent.
 */
@Component({
  selector: 'app-password-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <h2 mat-dialog-title>{{ 'DMS.PASSWORD.TITLE' | translate: { name: data.displayName } }}</h2>
    <mat-dialog-content class="aca-password">
      <mat-form-field appearance="outline">
        <mat-label>{{ 'DMS.PASSWORD.NEW' | translate }}</mat-label>
        <input matInput [type]="shown ? 'text' : 'password'" [(ngModel)]="password" autocomplete="new-password" cdkFocusInitial />
        <button
          mat-icon-button
          matSuffix
          type="button"
          (click)="shown = !shown"
          [attr.aria-label]="(shown ? 'DMS.PASSWORD.HIDE' : 'DMS.PASSWORD.SHOW') | translate"
        >
          <mat-icon>{{ shown ? 'visibility_off' : 'visibility' }}</mat-icon>
        </button>
        <mat-hint>{{ 'DMS.PASSWORD.RULE' | translate: { min: minLength } }}</mat-hint>
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>{{ 'DMS.PASSWORD.REPEAT' | translate }}</mat-label>
        <input matInput [type]="shown ? 'text' : 'password'" [(ngModel)]="repeat" autocomplete="new-password" (keydown.enter)="confirm()" />
      </mat-form-field>
      <p class="aca-password__error" *ngIf="mismatch()" role="alert">{{ 'DMS.PASSWORD.MISMATCH' | translate }}</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="close()">{{ 'DMS.WF_DIALOG.CANCEL' | translate }}</button>
      <button mat-flat-button color="primary" type="button" (click)="confirm()" [disabled]="!isValid()">{{ 'DMS.PASSWORD.SAVE' | translate }}</button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .aca-password {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: min(380px, 80vw);
      }
      .aca-password__error {
        margin: 0;
        font-size: 13px;
        color: var(--mat-sys-error, #b3261e);
      }
    `
  ]
})
export class PasswordDialogComponent {
  /** Long enough to rule out the guessable; Alfresco itself sets no rule. */
  static readonly MIN_LENGTH = 8;

  readonly data = inject<PasswordDialogData>(MAT_DIALOG_DATA);
  private readonly ref = inject(MatDialogRef<PasswordDialogComponent>);

  readonly minLength = PasswordDialogComponent.MIN_LENGTH;
  password = '';
  repeat = '';
  shown = false;

  /** Both typed and different — said only once the second one has something in it. */
  mismatch(): boolean {
    return this.repeat.length > 0 && this.password !== this.repeat;
  }

  isValid(): boolean {
    return this.password.length >= this.minLength && this.password === this.repeat && this.password.trim() === this.password;
  }

  confirm(): void {
    if (this.isValid()) {
      this.ref.close(this.password);
    }
  }

  close(): void {
    this.ref.close();
  }
}
